'use client'

import React, { useMemo, useRef, useEffect } from 'react'
import { ISeriesApi, IChartApi, LineStyle, LineWidth, LineSeries, UTCTimestamp } from 'lightweight-charts'
import { 
  FibonacciRetracement, 
  Point, 
  FibonacciLevel,
  calculateFibonacciPrices,
  DEFAULT_FIBONACCI_RETRACEMENT_LEVELS
} from '../../types/drawing'

interface FibonacciRetracementProps {
  drawing: FibonacciRetracement
  chart: IChartApi
  isActive: boolean
  onUpdate?: (drawing: FibonacciRetracement) => void
  onComplete?: (drawing: FibonacciRetracement) => void
}

/**
 * Fibonacci Retracement Drawing Component
 * 
 * Based on TradingView's Fibonacci Retracement tool:
 * - Displays key Fibonacci ratios: 23.6%, 38.2%, 50%, 61.8%, 78.6%
 * - Shows prices and labels for each level
 * - Supports extending lines left/right
 * - Customizable background colors and line styles
 * - Reversible direction (useful for different trend orientations)
 */
const FibonacciRetracementComponent: React.FC<FibonacciRetracementProps> = ({
  drawing,
  chart,
  isActive,
  onUpdate,
  onComplete
}) => {
  const seriesRefs = useRef<Map<string, ISeriesApi<'Line'>>>(new Map())
  const containerRef = useRef<HTMLDivElement>(null)

  // Calculate Fibonacci levels based on start and end points
  const fibLevels = useMemo(() => {
    if (drawing.points.length < 2) return []
    
    const [startPoint, endPoint] = drawing.points
    return calculateFibonacciPrices(
      startPoint,
      endPoint,
      drawing.levels.length > 0 ? drawing.levels : DEFAULT_FIBONACCI_RETRACEMENT_LEVELS,
      drawing.reverse
    )
  }, [drawing.points, drawing.levels, drawing.reverse])

  // Create or update line series for each Fibonacci level
  useEffect(() => {
    if (!chart || drawing.points.length < 2) return

    const [startPoint, endPoint] = drawing.points
    
    // Clear existing series
    seriesRefs.current.forEach(series => {
      try {
        if (chart && series) {
          chart.removeSeries(series)
        }
      } catch (error) {
        // Safely ignore cleanup errors when chart is destroyed
        console.warn('Failed to remove series during cleanup:', error)
      }
    })
    seriesRefs.current.clear()

    // Create new series for each visible level
    fibLevels.forEach((level, index) => {
      if (!level.isVisible) return

      // Create line series for this level
      const lineSeries = chart.addSeries(LineSeries, {
        color: level.color || drawing.style.lineColor,
        lineWidth: drawing.style.lineWidth as LineWidth,
        lineStyle: drawing.style.lineStyle === 'dashed' ? LineStyle.Dashed : 
                   drawing.style.lineStyle === 'dotted' ? LineStyle.Dotted : 
                   LineStyle.Solid,
        title: `Fib ${level.label}`,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false
      })

      // Calculate line points with proper time format
      const lineData = [
        {
          time: (drawing.extend.left ? 0 : startPoint.time) as UTCTimestamp,
          value: level.price
        },
        {
          time: (drawing.extend.right ? 999999999999 : endPoint.time) as UTCTimestamp,
          value: level.price
        }
      ]

      lineSeries.setData(lineData)
      seriesRefs.current.set(`level-${index}`, lineSeries)
    })

    // Add main trend line if enabled
    if (drawing.style.extend) {
      const trendSeries = chart.addSeries(LineSeries, {
        color: drawing.style.lineColor,
        lineWidth: (drawing.style.lineWidth + 1) as LineWidth,
        lineStyle: LineStyle.Solid,
        title: 'Fib Trend',
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false
      })

      const trendData = [
        { time: startPoint.time as UTCTimestamp, value: startPoint.price },
        { time: endPoint.time as UTCTimestamp, value: endPoint.price }
      ]

      trendSeries.setData(trendData)
      seriesRefs.current.set('trend', trendSeries)
    }

    return () => {
      seriesRefs.current.forEach(series => {
        try {
          if (chart && series) {
            chart.removeSeries(series)
          }
        } catch (error) {
          // Safely ignore cleanup errors when chart is destroyed
          console.warn('Failed to remove series during cleanup:', error)
        }
      })
      seriesRefs.current.clear()
    }
  }, [chart, drawing, fibLevels])

  // Render price labels and level text
  const renderLabels = () => {
    if (!drawing.showLabels && !drawing.showPrices) return null

    return (
      <div 
        ref={containerRef}
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginRight: '12px',
          marginTop: '110px',
          marginLeft: '10px',
          // right: '0',
        }}
      >
        {fibLevels.map((level, index) => {
          if (!level.isVisible) return null

          return (
            <div
              key={`label-${index}`}
              style={{
                position: 'relative',
                color: level.color || drawing.style.textColor,
                fontSize: `${drawing.style.fontSize}px`,
                fontWeight: drawing.style.fontSize > 12 ? 'bold' : 'normal',
                background: drawing.showBackground ? 'rgba(0, 0, 0, 0.7)' : 'transparent',
                padding: drawing.showBackground ? '2px 6px' : '0',
                borderRadius: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              {drawing.showLabels && level.label}
              {drawing.showLabels && drawing.showPrices && ' - '}
              {drawing.showPrices && `$${level.price.toFixed(4)}`}
            </div>
          )
        })}
      </div>
    )
  }

  // Handle mouse interactions for editing
  const handleMouseEvents = () => {
    if (!isActive || !chart) return

    // TODO: Implement mouse event handlers for editing
    // - Click and drag to move levels
    // - Right-click for context menu
    // - Handle point manipulation
  }

  return (
    <>
      {renderLabels()}
      {isActive && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'auto',
            cursor: 'crosshair'
          }}
          onMouseDown={handleMouseEvents}
        />
      )}
    </>
  )
}

export default FibonacciRetracementComponent

// Fibonacci Retracement Factory
export const createFibonacciRetracement = (
  id: string,
  startPoint: Point,
  endPoint: Point,
  options?: Partial<FibonacciRetracement>
): FibonacciRetracement => {
  const levels = calculateFibonacciPrices(
    startPoint,
    endPoint,
    DEFAULT_FIBONACCI_RETRACEMENT_LEVELS,
    options?.reverse || false
  )

  return {
    id,
    type: 'fibonacci-retracement',
    points: [startPoint, endPoint],
    levels,
    showPrices: true,
    showLabels: true,
    showBackground: false,
    reverse: false,
    extend: {
      left: false,
      right: true
    },
    style: {
      lineColor: '#2962FF',
      lineWidth: 1,
      lineStyle: 'solid',
      textColor: '#E0E3EB',
      fontSize: 12
    },
    isSelected: false,
    isVisible: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    zIndex: 1,
    ...options
  }
}

// Utility functions for Fibonacci calculations
export const getFibonacciLevelPrice = (
  startPrice: number,
  endPrice: number,
  ratio: number,
  reverse: boolean = false
): number => {
  const priceDifference = endPrice - startPrice
  return reverse
    ? endPrice - (ratio * priceDifference)
    : startPrice + (ratio * priceDifference)
}

export const isPointNearFibLevel = (
  point: Point,
  fibDrawing: FibonacciRetracement,
  tolerance: number = 0.01
): FibonacciLevel | null => {
  for (const level of fibDrawing.levels) {
    if (Math.abs(point.price - level.price) <= tolerance) {
      return level
    }
  }
  return null
}

// Popular Fibonacci level configurations
export const FIBONACCI_PRESETS = {
  standard: [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1],
  extended: [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.272, 1.414, 1.618],
  crypto: [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.272, 1.618, 2.618],
  conservative: [0, 0.382, 0.5, 0.618, 1]
} 